import { Http } from '@/utils/request';
import { AppListParams } from './type.d';
import { APP_LIST_TYPE } from './type/appList';
import { CreateAppType } from './type/createApp';
import { UpdateVersionTypes } from './type/updateVersion';
import { AppVersionType } from './type/appVersion';
import { GetReplyFeature } from './type/appApi';
import { SaveAppConfigType } from './type/saveApiConfig';
import { AppLogListReplyLog } from './type/appLog';
import {
  CreateBannerApi,
  EditBannerApi,
  FetchBannerListApi,
  FetchValidationJsonConfigApi,
  UpdateAppApiConfigV2Api,
  UpdateValidationJsonConfigApi,
} from './type/appBanner';

import { FetchAppApiListV2Api } from './type/apiConfig';

import { noDataFilter } from '@/utils/tool';

export const fetchAppList = async (params: AppListParams) => {
  let options = {};
  if ('appId' in params) {
    options = {
      searchParams: {
        appId: params.appId,
      },
    };
  }
  return await Http.get(`open-app-bff/users/me/teams/${params.teamId}/apps`, options).json<
    APIRes<APP_LIST_TYPE[]>
  >();
};
// 获取审核成功日志
export const fetchReviewLogs = async (params: AppListParams) => {
  return await Http.get(`open-app-bff/users/me/reviewlogs`, {
    searchParams: params,
  }).json<APIRes<any>>();
};

// 已读审核成功日志
export const readReviewLogs = async (params: { id: string; isView: number }) => {
  return await Http.put(`open-app-bff/users/me/reviewlogs/${params.id}`, {
    json: params,
  }).json<APIRes<any>>();
};

//获取应用详情
export const fetchAppDetail = async (params: AppListParams) => {
  return await Http.get(`open-app-bff/users/me/teams/${params.teamId}/apps/${params.appId}`).json<
    APIRes<APP_LIST_TYPE>
  >();
};

//创建应用
export const createApp = async (params: CreateAppType) => {
  return await Http.post(`open-app-bff/users/me/teams/${params.teamId}/apps`, {
    json: params,
  }).json<APIRes<never>>();
};

//更新应用
export const updateApp = async (
  params: CreateAppType & {
    teamId: string;
    appId: string;
  }
) => {
  return await Http.put(`open-app-bff/users/me/teams/${params.teamId}/apps/${params.appId}`, {
    json: params,
  }).json<APIRes<never>>();
};

//创建应用审核
export const updateAppReviews = async (
  params: CreateAppType & {
    teamId: string;
    appId: string;
  }
) => {
  return await Http.post(
    `open-app-bff/users/me/teams/${params.teamId}/apps/${params.appId}/reviews`,
    {
      json: params,
    }
  ).json<APIRes<never>>();
};

//获取版本信息
export const getAppVersion = async (params: UpdateVersionTypes) => {
  return await Http.get(
    `open-app-bff/users/me/teams/${params.teamId}/apps/${params.appId}/versions?version=${params.version}`
  ).json<APIRes<any>>();
};

//更新版本信息
export const updateAppVersion = async (params: UpdateVersionTypes) => {
  return await Http.put(
    `open-app-bff/users/me/teams/${params.teamId}/apps/${params.appId}/versions`,
    {
      json: params,
    }
  ).json<APIRes<never>>();
};

//检测App是否能发布
export const checkAppPublic = async (
  params: Omit<UpdateVersionTypes, 'version' | 'versionDesc'>
) => {
  return await Http.get(
    `open-app-bff/users/me/teams/${params.teamId}/apps/${params.appId}/check-release`
  ).json<APIRes<APP_LIST_TYPE>>();
};

//APP发布审核
export const pubicAppReview = async (params: UpdateVersionTypes) => {
  return await Http.post(
    `open-app-bff/users/me/teams/${params.teamId}/apps/${params.appId}/releases`,
    {
      json: params,
    }
  ).json<APIRes<never>>();
};

//APP版本信息
export const fetchAppVersion = async (params: { currentVersion: string }) => {
  return await Http.get(
    `open-app-bff/users/me/api/versions?currentVersion=${params.currentVersion}`
  ).json<APIRes<AppVersionType[]>>();
};

//APP API信息
export const fetchAppApiList = async (params: { teamId: string; appId: string }) => {
  return await Http.get(
    `open-app-bff/users/me/teams/${params.teamId}/apps/${params.appId}/features`
  ).json<APIRes<GetReplyFeature>>();
};

//保存API 配置
export const saveAppApiConfig = async (params: SaveAppConfigType) => {
  return await Http.put(
    `open-app-bff/users/me/teams/${params.teamId}/apps/${params.appId}/features`,
    {
      json: params,
    }
  ).json<APIRes<never>>();
};

//获取报表相关
export const fetchAppReport = async (params: {
  teamId: string;
  appId: string;
  timeZone: number;
  from: number;
  through: number;
}) => {
  return await Http.get(
    `open-app-bff/users/me/teams/${params.teamId}/apps/${params.appId}/reports/installation`,
    {
      searchParams: params,
    }
  ).json<APIRes<any>>();
};

/**
 * @async
 * @method
 * @description 获取【app管理->api设置】配置项列表
 * @param {FetchAppApiListV2Api.RequestProps} { teamId, appId } 接口所需数据
 * @returns {Promise<APIRes<FetchAppApiListV2Api.ReponseProps[], unknown>>}
 */
export const fetchAppApiListV2 = async ({
  teamId,
  appId,
}: FetchAppApiListV2Api.RequestProps): Promise<
  APIRes<FetchAppApiListV2Api.ReponseProps[], unknown>
> =>
  await Http.get(`open-app-bff/users/me/teams/${teamId}/apps/${appId}/functions`).json<
    APIRes<FetchAppApiListV2Api.ReponseProps[], unknown>
  >();

/**
 * @async
 * @method
 * @description 获取【app管理->api设置->设置当前应用api配置项】
 * @param {UpdateAppApiConfigV2Api.RequestProps} { teamId, appId, groupId, data } 接口所需数据
 * @returns {Promise<APIRes<UpdateAppApiConfigV2Api.ReponseProps, unknown>>}
 */
export const updateAppApiConfig = async ({
  teamId,
  appId,
  groupId,
  data,
}: UpdateAppApiConfigV2Api.RequestProps): Promise<
  APIRes<UpdateAppApiConfigV2Api.ReponseProps, unknown>
> =>
  await Http.put(`open-app-bff/users/me/teams/${teamId}/apps/${appId}/functions`, {
    json: {
      groupId,
      data,
    },
  }).json<APIRes<UpdateAppApiConfigV2Api.ReponseProps, unknown>>();

/**
 * @async
 * @method
 * @description 获取【app管理->校验配置->设置当前应用json配置】
 * @param {FetchValidationJsonConfigApi.RequestProps} { teamId, appId, verifyConfig } 接口所需数据
 * @returns {Promise<APIRes<FetchValidationJsonConfigApi.ReponseProps, unknown>>}
 */
export const fetchValidationJsonConfig = async ({
  teamId,
  appId,
}: FetchValidationJsonConfigApi.RequestProps): Promise<
  APIRes<FetchValidationJsonConfigApi.ReponseProps, unknown>
> =>
  await Http.get(
    `open-app-bff/users/me/teams/${teamId}/apps/${appId}/settings/address_verification`
  ).json<APIRes<FetchValidationJsonConfigApi.ReponseProps, unknown>>();

/**
 * @async
 * @method
 * @description 获取【app管理->校验配置->设置当前应用json配置】
 * @param {UpdateValidationJsonConfigApi.RequestProps} { teamId, appId, verifyConfig } 接口所需数据
 * @returns {Promise<APIRes<UpdateValidationJsonConfigApi.ReponseProps, unknown>>}
 */
export const updateValidationJsonConfig = async ({
  teamId,
  appId,
  verifyConfig,
}: UpdateValidationJsonConfigApi.RequestProps): Promise<
  APIRes<UpdateValidationJsonConfigApi.ResponseProps, unknown>
> =>
  await Http.put(
    `open-app-bff/users/me/teams/${teamId}/apps/${appId}/settings/address_verification`,
    {
      json: {
        verifyConfig,
      },
    }
  ).json<APIRes<UpdateValidationJsonConfigApi.ResponseProps, unknown>>();

/**
 * @async
 * @method
 * @description 创建banner
 * @param {FetchBannerListApi.RequestProps} { teamId, appId, verifyConfig } 接口所需数据
 * @returns {Promise<APIRes<FetchBannerListApi.ResponseProps, unknown>>}
 */
export const fetchBannerList = async ({
  teamId,
  appId,
  ...rest
}: FetchBannerListApi.RequestProps): Promise<
  APIPageRes<FetchBannerListApi.ResponseProps, unknown>
> =>
  await Http.get(`open-app-bff/users/me/teams/${teamId}/apps/${appId}/banners`, {
    searchParams: noDataFilter(rest),
  }).json<APIPageRes<FetchBannerListApi.ResponseProps, unknown>>();

/**
 * @async
 * @method
 * @description 修改banner
 * @param {EditBannerApi.RequestProps} { teamId, appId, verifyConfig } 接口所需数据
 * @returns {Promise<APIRes<EditBannerApi.ResponseProps, unknown>>}
 */

/**
 * @async
 * @method
 * @description 创建banner
 * @param {CreateBannerApi.RequestProps} { teamId, appId, verifyConfig } 接口所需数据
 * @returns {Promise<APIRes<CreateBannerApi.ResponseProps, unknown>>}
 */
export const createBanner = async ({
  teamId,
  appId,
  ...rest
}: CreateBannerApi.RequestProps): Promise<any> =>
  await Http.post(`open-app-bff/users/me/teams/${teamId}/apps/${appId}/banners`, {
    json: noDataFilter(rest),
  }).json();

//获取应用的调用日志列表
export const getAppLogReview = async ({
  teamId,
  appId,
  ...reset
}: {
  teamId: string;
  appId: string;
}) => {
  return await Http.get(`open-app-bff/users/me/teams/${teamId}/apps/${appId}/logs`, {
    searchParams: { ...reset },
  }).json<PaginationResponseType<AppLogListReplyLog>>();
};

export const editBanner = async ({
  teamId,
  appId,
  id,
  ...rest
}: EditBannerApi.RequestProps): Promise<APIRes<EditBannerApi.ResponseProps, unknown>> =>
  await Http.put(`open-app-bff/users/me/teams/${teamId}/apps/${appId}/banners/${id}`, {
    json: noDataFilter(rest),
  }).json<APIRes<EditBannerApi.ResponseProps, unknown>>();
